import { Link, useLocation, useNavigate } from "react-router-dom";
import { ThemeToggle } from "../theme/theme-toggle";
import {
  Menu,
  X,
  Dumbbell,
  Settings,
  LogOut,
  User,
  ChevronDown,
  History,
  ArrowRight,
  Plus,
  Loader2,
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../utils/cn";
import { useState, useEffect } from "react";
import { supabase } from "../../integrations/supabase/client";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { motion, AnimatePresence } from "framer-motion";
import { createWorkout } from "../../utils/factories";
import { workoutService } from "../../services/workout";

interface HeaderProps {
  className?: string;
}

export function Header({ className }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine if we're on the main workout page or history page
  const isOnWorkoutsPage = location.pathname === "/";
  const isOnHistoryPage = location.pathname === "/workout-history";

  // Track scroll position to apply header effects
  useEffect(() => {
    const updateScrollState = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", updateScrollState);
    updateScrollState(); // Initial check

    return () => window.removeEventListener("scroll", updateScrollState);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Failed to sign out");
    }
  };

  // Smart header click handler
  const handleHeaderClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // If we're on the workouts page, go to history
    if (isOnWorkoutsPage) {
      navigate("/workout-history");
    }
    // If we're on the history page, go to workouts
    else if (isOnHistoryPage) {
      navigate("/");
    }
    // If we're on any other page, default to workouts
    else {
      navigate("/");
    }
  };

  // Handle creating a new workout
  const handleCreateWorkout = async () => {
    try {
      setIsCreating(true);
      const newWorkout = createWorkout();
      const savedWorkout = await workoutService.createWorkout(newWorkout);
      toast.success("New workout created");
      navigate(`/workout/edit/${savedWorkout.id}`);
    } catch (error) {
      console.error("Error creating workout:", error);
      toast.error("Failed to create workout");
    } finally {
      setIsCreating(false);
    }
  };

  // Determine which icon and text to show based on current page
  const isHistoryView = isOnWorkoutsPage;
  const headerText = isHistoryView ? "Workout History" : "My Workouts";
  const labelText = isHistoryView ? "view history" : "view workouts";

  return (
    <header
      className={cn("sticky top-0 z-40 transition-all duration-200", className)}
    >
      {/* Glassy background effect that appears when scrolled */}
      <div
        className={cn(
          "absolute inset-0 transition-all duration-300",
          isScrolled
            ? "bg-background/80 backdrop-blur-md border-b shadow-sm"
            : "bg-background border-b"
        )}
      ></div>

      <div className="container relative flex h-16 items-center justify-between">
        {/* Logo - Left aligned with smart navigation */}
        <div className="flex-none">
          <button
            onClick={handleHeaderClick}
            className="font-bold flex items-center hover:opacity-90 transition-opacity relative group text-left"
            aria-label={headerText}
          >
            <div className="relative w-8 h-8 mr-2 flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={isHistoryView ? "history" : "dumbbell"}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="absolute"
                >
                  {isHistoryView ? (
                    <History className="h-6 w-6 text-primary" />
                  ) : (
                    <Dumbbell className="h-6 w-6 text-primary" />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex flex-col items-start">
              <div className="h-8 flex items-center">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={headerText}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{
                      duration: 0.3,
                      ease: "easeInOut",
                      delay: 0.05,
                    }}
                    className="text-xl whitespace-nowrap"
                  >
                    {headerText}
                  </motion.span>
                </AnimatePresence>
              </div>

              {/* Subtle label with arrow - left aligned */}
              <div className="flex items-center text-xs text-muted-foreground">
                <span>{labelText}</span>
                <ArrowRight className="h-3 w-3 ml-1" />
              </div>
            </div>
          </button>
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>

        {/* Right side controls */}
        <div className="hidden md:flex items-center gap-2">
          {/* New Workout Button - Only on workouts pages */}
          {isOnWorkoutsPage && (
            <Button
              onClick={handleCreateWorkout}
              disabled={isCreating}
              size="sm"
              className="mr-2"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  New Workout
                </>
              )}
            </Button>
          )}

          {/* Theme toggle */}
          <ThemeToggle />

          {/* Account dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center">
                <User className="h-4 w-4 mr-2" />
                <span>Account</span>
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center w-full">
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t relative">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* Add New Workout button to mobile menu when on workouts pages */}
            {isOnWorkoutsPage && (
              <Button
                onClick={handleCreateWorkout}
                disabled={isCreating}
                className="w-full justify-start mb-2"
                variant="default"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5 mr-3" />
                    New Workout
                  </>
                )}
              </Button>
            )}

            <div className="flex items-center justify-between px-3 py-2">
              <span>Theme</span>
              <ThemeToggle />
            </div>
            <Link
              to="/settings"
              className="flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMenuOpen(false)}
            >
              <Settings className="h-5 w-5" />
              <span className="ml-3">Settings</span>
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start hover:bg-muted mt-2"
              onClick={handleSignOut}
            >
              <LogOut className="h-5 w-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
